import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { ChevronDown, Plus, X } from "lucide-react";

// Iter 275 — Reason filter: every reason with its parcel count, type to
// narrow, pick to filter (exact match), or add a brand-new reason.
export const ReasonFilter = ({ value, exact, onChange, onReasonsChanged }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/order-register/reasons/summary`, { headers: getAuthHeader() });
      setList(r.data.reasons || []);
    } catch { /* keep last list */ }
  };
  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const t = (value || "").trim().toLowerCase();
  const shown = t && !exact ? list.filter(x => x.reason.toLowerCase().includes(t)) : list;
  const canAdd = t && !list.some(x => x.reason.toLowerCase() === t);

  const addNew = async () => {
    try {
      const r = await axios.post(`${API}/order-register/reasons`, { name: value.trim() }, { headers: getAuthHeader() });
      toast.success(r.data.exists ? "Reason already exists" : `Added reason "${r.data.name}"`);
      onChange(r.data.name, true);
      onReasonsChanged?.();
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add reason"); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className={`flex h-8 w-48 items-center rounded-md border bg-white pr-1 ${exact ? "border-indigo-400 ring-1 ring-indigo-200" : "border-input"}`}>
        <input value={value} autoComplete="off" name="or-reason-filter" placeholder="Reason"
          onFocus={() => setOpen(true)}
          onChange={(e) => { onChange(e.target.value, false); setOpen(true); }}
          className="h-full w-full bg-transparent px-3 text-xs outline-none"
          data-testid="or-reason-filter" />
        {value ? (
          <button type="button" onClick={() => onChange("", false)} className="p-0.5 text-slate-400 hover:text-slate-700"
                  data-testid="or-reason-clear"><X className="h-3.5 w-3.5" /></button>
        ) : (
          <button type="button" onClick={() => setOpen(o => !o)} className="p-0.5 text-slate-400 hover:text-slate-700"
                  data-testid="or-reason-toggle"><ChevronDown className="h-3.5 w-3.5" /></button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-72 w-64 overflow-auto rounded-md border bg-white py-1 shadow-lg"
             data-testid="or-reason-menu">
          {shown.length === 0 && !canAdd && (
            <div className="px-3 py-2 text-xs text-slate-400">No reasons yet</div>
          )}
          {shown.map(x => (
            <button key={x.reason} type="button"
              onClick={() => { onChange(x.reason, true); setOpen(false); }}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition-colors hover:bg-slate-50 ${
                exact && x.reason.toLowerCase() === t ? "bg-indigo-50 font-semibold text-indigo-700" : "text-slate-700"}`}
              data-testid={`or-reason-opt-${x.reason.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="truncate">{x.reason}</span>
              <span className={`ml-2 rounded-full px-1.5 text-[10px] font-bold ${x.count ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-400"}`}>
                {x.count}
              </span>
            </button>
          ))}
          {canAdd && (
            <button type="button" onClick={addNew}
              className="flex w-full items-center gap-1 border-t px-3 py-1.5 text-left text-xs text-emerald-700 hover:bg-emerald-50"
              data-testid="or-reason-add">
              <Plus className="h-3.5 w-3.5" /> Add "{value.trim()}" as new reason
            </button>
          )}
        </div>
      )}
    </div>
  );
};
